// M6 — Fetch wrapper for labourcore-api (localhost:3010).
// Single-flight 401 refresh, standard error envelope, typed ApiError.

// In dev, Vite proxies /auth, /rpc, /api to localhost:3010.
// In production, VITE_API_BASE_URL points to the real API.
const BASE_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL)
  || "";

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/* ── Token storage ─────────────────────────────────────────────── */

/* Exported so the cross-tab sync can filter `storage` events on exactly these
   keys (GH #198/#207/#209). Duplicating the strings in a listener would drift
   silently the first time a key is renamed. */
export const TOKEN_KEYS = { access: "lc:access_token", refresh: "lc:refresh_token" } as const;

export function getAccessToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEYS.access); } catch { return null; }
}
export function getRefreshToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEYS.refresh); } catch { return null; }
}
export function setTokens(access: string, refresh: string): void {
  try {
    localStorage.setItem(TOKEN_KEYS.access, access);
    localStorage.setItem(TOKEN_KEYS.refresh, refresh);
  } catch { /* SSR / incognito */ }
}
export function clearTokens(): void {
  try {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
  } catch { /* ignore */ }
}

/* ── Safe fetch + JSON parse ───────────────────────────────────── */

export interface SafeJsonResult<T = unknown> {
  ok: boolean;
  status: number;
  json: T | null;
  /** Set when the request itself failed (offline/DNS/server unreachable) or
   * the response body wasn't valid JSON (e.g. a dev server restart cut the
   * response short, or a proxy/gateway returned an HTML error page).
   * Callers should show this generic message and never the raw
   * fetch/JSON.parse error text — that's a stack-trace-shaped string, not
   * copy a user can act on. */
  transportError?: string;
}

export async function fetchJsonSafe<T = unknown>(
  url: string,
  init?: RequestInit,
): Promise<SafeJsonResult<T>> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    return {
      ok: false,
      status: 0,
      json: null,
      transportError: "Unable to reach the server. Check your connection and try again.",
    };
  }
  let json: T | null = null;
  try {
    json = (await res.json()) as T;
  } catch {
    return {
      ok: false,
      status: res.status,
      json: null,
      transportError: res.ok
        ? "Unexpected response from the server. Please try again."
        : `Something went wrong (HTTP ${res.status}). Please try again.`,
    };
  }
  return { ok: res.ok, status: res.status, json };
}

/* ── Parse error from response ─────────────────────────────────── */

/**
 * Last resort, when the server sent no readable body at all.
 *
 * "HTTP 403" tells a user nothing they can act on. These say what happened in
 * words, and — where there is one — what to do about it. Deliberately short:
 * this text usually lands in a toast, not a page.
 */
function httpFallback(status: number): string {
  switch (status) {
    case 400: return "That request was not valid.";
    case 401: return "Your session has expired. Sign in again.";
    case 403: return "You do not have permission to do that.";
    case 404: return "That is no longer available.";
    case 409: return "That has already been done.";
    case 429: return "Too many attempts. Wait a moment and try again.";
    default:  return status >= 500
      ? "Something went wrong at our end. Try again shortly."
      : `Request failed (${status}).`;
  }
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = await res.json();
    /* `message` BEFORE `error`, and the order is the whole fix.
     *
     * Two error shapes reach here:
     *
     *   ours      { error: "You may only dispatch requirements you posted",
     *               code: "not_own_record" }        ← real text in `error`
     *   Fastify   { statusCode: 403, error: "Forbidden",
     *               message: "You may only …" }     ← real text in `message`
     *
     * Checking `error` first meant Fastify's generic HTTP word ALWAYS won, so
     * a carefully written server message was replaced on screen by the single
     * word "Forbidden". The API was doing its job; the client was discarding
     * the answer.
     *
     * This order satisfies both: ours has no `message` and falls through to
     * `error`; Fastify's `message` is taken before its generic `error`. */
    return new ApiError(
      res.status,
      body.code || `HTTP_${res.status}`,
      body.message || body.error || httpFallback(res.status),
      body.details,
    );
  } catch {
    return new ApiError(res.status, `HTTP_${res.status}`, httpFallback(res.status));
  }
}

/* ── Single-flight refresh ─────────────────────────────────────── */

let _refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const json = await res.json();
    if (json.access_token && json.refresh_token) {
      setTokens(json.access_token, json.refresh_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

async function refreshTokens(): Promise<boolean> {
  if (!_refreshPromise) {
    _refreshPromise = doRefresh().finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

/* ── Authed fetch with 401 retry ───────────────────────────────── */

/* The acting context — which company, and which side of the marketplace. Read
   from the Redux store rather than passed in, because this is not a component.

   THE HEADERS ARE SET HERE AND NOWHERE ELSE. Every API call already goes
   through authedFetch, so a new endpoint gets them for free. The last time a
   value like this was set per call site we ended up with four different
   behaviours and a security defect — see Live-Defects-Log #4 and #27. */
import { currentActingContext } from "../context/store";

export async function authedFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> || {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  /* Null until hydration completes, so no request goes out claiming a context
     the server has not yet confirmed. The API treats their absence as "behave
     exactly as before", which is what lets the two sides deploy independently. */
  const acting = currentActingContext();
  if (acting) {
    /* X-LC-Company is OMITTED for a person-tier. An operative has no company,
       and sending an empty string would be a malformed uuid rather than an
       absence — the server distinguishes the two. */
    if (acting.companyId) headers["X-LC-Company"] = acting.companyId;
    headers["X-LC-Tier"] = acting.tier;
  }

  let res = await fetch(url, { ...init, headers });

  if (res.status === 401) {
    const ok = await refreshTokens();
    if (!ok) {
        clearTokens();
      /* BOTH names, deliberately. This path — refresh failed, tokens cleared —
         used to fire only "lc:auth-changed" (colon), while AuthContext and every
         other listener in the app use "lc-auth-changed" (hyphen). So a genuinely
         dead session never reached the UI and the tab kept rendering the
         authenticated shell. `_notifyAuthChange` in supabase-client.ts already
         fires both; this is the one place that did not. */
      window.dispatchEvent(new Event("lc:auth-changed"));
      window.dispatchEvent(new Event("lc-auth-changed"));
      throw new ApiError(401, "SESSION_EXPIRED", "Session expired — please sign in again");
    }
    // Retry with new token
    const newToken = getAccessToken();
    if (newToken) headers["Authorization"] = `Bearer ${newToken}`;
    res = await fetch(url, { ...init, headers });
  }

  return res;
}

/* ── Convenience helpers ───────────────────────────────────────── */

export async function apiPost<T = unknown>(path: string, body?: unknown): Promise<T> {
  /* Content-Type is set only when there IS a body. Fastify's JSON parser
     rejects an empty body when the header claims application/json — it returns
     "Body cannot be empty when content-type is set to 'application/json'",
     which surfaces as a 500. So a bodyless apiPost() against any Fastify route
     failed. Found while adding the score-token revoke endpoint. */
  const hasBody = body !== undefined;
  const res = await authedFetch(`${BASE_URL}${path}`, {
    method: "POST",
    ...(hasBody ? { headers: { "Content-Type": "application/json" } } : {}),
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

/** DELETE with no body — same Content-Type caveat as apiPost. */
export async function apiDelete<T = unknown>(path: string): Promise<T> {
  const res = await authedFetch(`${BASE_URL}${path}`, { method: "DELETE" });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

/** PUT with a body. */
export async function apiPut<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await authedFetch(`${BASE_URL}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export async function apiGet<T = unknown>(path: string, params?: Record<string, string>): Promise<T> {
  let url = `${BASE_URL}${path}`;
  if (params) {
    const sp = new URLSearchParams(params);
    url += `?${sp.toString()}`;
  }
  const res = await authedFetch(url);
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export async function apiPatch<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await authedFetch(`${BASE_URL}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export { BASE_URL };
